import { ChangeDetectorRef, Component, effect, inject, OnDestroy, OnInit, HostListener } from '@angular/core';
import { NavigationEnd, Router, RouterModule, RouterOutlet } from '@angular/router';
import { UserService } from './services/user.service';
import { ToastComponent } from "./common/toast";
import { Loader } from './common/loader';
import { GlobalService } from './services/global.service';
import { MatIconModule } from '@angular/material/icon';
import { UnSubscriber } from './common/unsubscriber';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TournamentService } from './services/tournament.service';
import { CommonDataStore } from './state-management/global.store';
import { Header } from "./common/header/header";
import { Sidebar } from "./common/sidebar/sidebar";
import { API_KEYS } from './state-management/api.config';
import { SecureStorageService } from './services/secure-storage.service';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DrawsheetService } from './services/drawsheet.service';
import { DynamicComponent } from './common/dynamic-component/dynamic-component';
import { LoaderService } from './services/loader.service';
import { ToastService } from './services/toastr.service';
import { forkJoin } from 'rxjs';
import { DynamicDialog } from './models/global.model';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { InputSelect } from './common/input-select/input-select';
import { ViewChild, TemplateRef } from '@angular/core';
import { BottomNav } from './common/bottom-nav/bottom-nav';
import { LayoutService } from './services/layout.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, RouterModule, ToastComponent, Loader, MatIconModule, FormsModule, CommonModule, Header, Sidebar, MatDialogModule, MatCheckboxModule, InputSelect, BottomNav],
  templateUrl: './app.html', styleUrl: './app.scss'
})
export class App extends UnSubscriber implements OnInit, OnDestroy {
  private router = inject(Router);
  public userService = inject(UserService);
  public tournamentService = inject(TournamentService);
  public globalService = inject(GlobalService);
  private cdr = inject(ChangeDetectorRef);
  private CommonDataStore = inject(CommonDataStore);
  private sessionService = inject(SecureStorageService);
  private dialog = inject(MatDialog);
  private drawsheetService = inject(DrawsheetService);
  private loaderService = inject(LoaderService);
  private toast = inject(ToastService);
  public layout = inject(LayoutService);

  // #region variable
  UserName: string = '';
  UserTitle: string = '';
  TournamentList: any[] = [];
  TournamentId: number = +(this.globalService.getItem('tournament_id') || 0);
  hoveredItem: string | null = null;
  userMenus: any[] = [];
  IsContainerHide: boolean = false;
  liveScoreMenu: any = null;
  isLiveScorePage: boolean = false;
  isDashboardView: boolean = false;
  isPublicRegisterPage: boolean = false;
  // #region Global Barcode Scanner
  private barcodeBuffer: string = '';
  private barcodeTimer: any;
  currentRingNo: any = '';

  // Bulk Transfer State
  @ViewChild('bulkMatchTransferTemplate') bulkMatchTransferTemplate!: TemplateRef<any>;
  remainingMatches: any[] = [];
  selectedMatchIds: number[] = [];
  targetRingNo: number = 0;
  ringList: any[] = [];
  // #endregion

  constructor() {
    super();
    // Central reactive hub for permission changes
    effect(async () => {
      const permsState = this.CommonDataStore[API_KEYS.PERMISSION]();
      if (permsState.loaded && permsState.data) {
        await this.processPermissionData(permsState.data);
      }
      this.cdr.detectChanges();
    });
  }

  ngOnInit() {

    // const decryptedId = this.globalService.decrypt(sessionStorage.getItem("id") || "");
    const decryptedId = this.sessionService.getStorage('id');
    const id = decryptedId ? Number(decryptedId) : null;

    if (id) {
      this.CommonDataStore.reloadapi(API_KEYS.PERMISSION, { id });
    }

    this.UserTitle = this.UserTitleName;
    this.listenForOutsideClicks();

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.IsContainerHide = this.globalService.HideContainer().includes(event.url);
        this.checkDashboardView();
        this.checkLiveScoreRoute();
        this.checkPublicRegisterRoute();
      }
    });
  }

  private processPermissionData(data: any) {
    const rawPermissions = data?.menuPermissions ?? [];

    // Update Global Permission Signal
    const allowed = rawPermissions.map((p: any) => p.RouterPath?.replace("/", "")).filter(Boolean);
    this.globalService.permission.set(allowed);

    const tournamentPermissions = data?.tournamentPermissions ?? [];
    this.globalService.tournamentPermissions.set(tournamentPermissions);

    // Update User Role Signal
    this.globalService.userRole.set(data?.safeUser?.role ?? []);

    // Build Hierarchy
    const viewable = rawPermissions.filter((m: any) => m.CanView);
    const unique = Array.from(new Map(viewable.map((m: any) => [m.MenuID, m])).values());
    const hierarchy = this.buildMenuHierarchy(unique);

    // Filter Sidebar Menus
    this.liveScoreMenu = hierarchy.find(m => m.MenuName?.toLowerCase().replace(/\s/g, '') === 'livescore');
    this.userMenus = this.updateMenuStates(hierarchy.filter(m => m.MenuID !== this.liveScoreMenu?.MenuID));

    this.checkDashboardView();
    this.checkLiveScoreRoute();
  }

  private updateMenuStates(menus: any[]): any[] {
    const activeMenuId = sessionStorage.getItem('active_menu_id');
    return menus.map((item: any) => {
      if (item.children?.length) {
        if (activeMenuId && item.MenuID.toString() === activeMenuId) {
          item.IsOpen = true;
        } else if (!activeMenuId) {
          item.IsOpen = item.children.some((child: any) =>
            this.router.url === child.RouterPath || this.router.url.startsWith(child.RouterPath)
          );
          if (item.IsOpen) sessionStorage.setItem('active_menu_id', item.MenuID);
        } else {
          item.IsOpen = false;
        }
      }
      return item;
    });
  }

  buildMenuHierarchy(menus: any[]): any[] {
    const menuMap = new Map<number, any>();
    const rootMenus: any[] = [];
    menus.sort((a, b) => a.iOrder - b.iOrder);

    menus.forEach(menu => {
      menuMap.set(menu.MenuID, { ...menu, children: [] });
    });

    menuMap.forEach((menu) => {
      const parentId = menu.ParentMenuID;
      if (parentId === '0' || !parentId) {
        rootMenus.push(menu);
      } else if (menuMap.has(parentId)) {
        menuMap.get(parentId).children.push(menu);
      }
    });
    return rootMenus;
  }

  GetTournamentList() {
    this.anotherSubscription = this.tournamentService.GetAllTournament().subscribe((res: any) => {
      if (res.data) {
        this.TournamentList = res.data;
        this.globalService.inputSelect(this.TournamentList, 'tournament_id', (item: any) => item.tournament_name);
      }
    });
  }

  onTournamentChange() {
    this.globalService.setItem('tournament_id', this.TournamentId);
    window.location.reload();
  }

  get UserTitleName() {
    const fullName = (this.globalService.getItem('CitizenName') || '')?.trim();
    const parts = fullName.split(/\s+/);
    return (parts[0]?.charAt(0).toUpperCase() || '') + (parts[1]?.charAt(0).toUpperCase() || '');
  }

  toggleNavLinks() {
    const navLinks = document.getElementById('navLinks');
    if (navLinks) navLinks.classList.toggle('active');
  }

  toggleSubmenu(event: Event) {
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    if (!target) return;
    if (target.classList.contains('open') && !!target.querySelector('.active-nav')) return;
    target.classList.toggle('open');
  }

  listenForOutsideClicks() {
    document.addEventListener('click', (event) => {
      if ((event.target as HTMLElement).closest('.has-submenu')) return;
      document.querySelectorAll('.has-submenu.open').forEach((el) => {
        if (!el.querySelector('.active-nav')) el.classList.remove('open');
      });
    });
  }

  isRouteActive(route: string): boolean {
    if (!route) return false;
    const url = this.router.url.split('?')[0];
    const target = route.startsWith('/') ? route : '/' + route;
    return url === target || url.startsWith(target + '/');
  }

  checkLiveScoreRoute() {
    const isDashboard = this.isRouteActive('/dashboard');
    this.isLiveScorePage = (this.liveScoreMenu?.RouterPath && this.isRouteActive(this.liveScoreMenu.RouterPath)) || isDashboard;
  }

  checkDashboardView() {
    this.isDashboardView = this.isRouteActive('/dashboard-view');
  }

  checkPublicRegisterRoute() {
    this.isPublicRegisterPage = this.isRouteActive('/public-register') || this.isRouteActive('/public-coach-register');
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }

    if (event.key === 'Enter') {
      if (this.barcodeBuffer.length > 2) {
        event.preventDefault();
        this.processGlobalBarcode(this.barcodeBuffer);
        this.barcodeBuffer = '';
      }
    } else {
      if (event.key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey) {
        this.barcodeBuffer += event.key;
        clearTimeout(this.barcodeTimer);
        this.barcodeTimer = setTimeout(() => {
          this.barcodeBuffer = '';
        }, 50);
      }
    }
  }

  private processGlobalBarcode(barcode: string) {
    const parts = barcode.split('-');
    if (parts.length >= 2) {
      const tId = Number(parts[0]);
      const cId = Number(parts[1]);
      const pNo = parts[2] || '-';

      // 1. Check if we are on a valid page (Drawsheet or Fixtures/Matches)
      const currentUrl = this.router.url;
      const isDrawsheetPage = currentUrl.includes('drawsheet'); // Catches /drawsheet and /view-drawsheet
      const isMatchesPage = currentUrl.includes('tournament-information/matches') || currentUrl.includes('match-fixture');

      if (!isDrawsheetPage && !isMatchesPage) {
        console.warn('Barcode scanned outside of allowed Drawsheet/Matches pages.');
        return; // Silently ignore or show a small toast if you want
      }

      // 2. Verify Tournament and Category IDs match the current page
      const params = this.router.routerState.snapshot.root.queryParams;
      const currentTId = Number(params['tournament_id']);
      const currentCId = Number(params['category_id'] || params['division_id']);

      if (currentTId !== tId || currentCId !== cId) {
        const dialogData = new DynamicDialog();
        dialogData.Title = 'Barcode Mismatch';
        dialogData.Message = 'The scanned barcode is for a different Tournament or Category. Please make sure you are on the correct Tournament or Category page to scan the barcode.';
        dialogData.Action = 'OK, I UNDERSTAND';
        dialogData.Width = '350px';

        this.dialog.open(DynamicComponent, {
          width: dialogData.Width,
          data: dialogData
        });
        return;
      }

      this.loaderService.requestStarted('Fetching bracket details...');

      forkJoin({
        matches: this.drawsheetService.getRemainingBracketMatches(tId, cId, pNo),
        tournament: this.tournamentService.GetTournamentById(tId),
        fixtures: this.tournamentService.GetTournamentFixturesList(tId),
        rings: this.tournamentService.GetRingsByTournamentId(tId)
      }).subscribe({
        next: (res: any) => {
          this.loaderService.requestEnded();
          const matches = res.matches?.data || [];
          if (matches.length === 0) {
            this.toast.info('No remaining matches found for this bracket.');
            return;
          }

          // Extract Context Names
          const tournamentData = res.tournament?.data || {};
          const tName = tournamentData.tournament_name || 'Tournament';

          const fixtureList = res.fixtures?.Table || [];
          const currentFixture = fixtureList.find((f: any) => Number(f.category_id) === cId);
          const cName = currentFixture?.category_name || matches[0]?.category_name || 'Category';

          // Extract rings for the dropdown
          const ringsRaw = res.rings?.Table || res.rings?.data || [];
          const ringDropdown = ringsRaw.map((r: any) => ({
            id: Number(r.ring_no || r.ring_number || r.id),
            name: `Ring ${r.ring_no || r.ring_number || r.id}`
          }));

          this.remainingMatches = matches || [];
          this.selectedMatchIds = [];
          this.targetRingNo = 0;
          this.ringList = ringDropdown;
          this.currentRingNo = currentFixture?.ring_no || matches[0]?.ring_number || matches[0]?.ring_no || 'N/A';

          const dialogData = new DynamicDialog();
          dialogData.Title = 'Bulk Match Transfer Ring';
          dialogData.Message = `${tName} | ${cName}`;
          dialogData.Content = this.bulkMatchTransferTemplate;
          dialogData.Action = 'TRANSFER MATCHES';
          dialogData.Close = 'CANCEL';
          dialogData.Width = '550px';

          this.globalService.OpenDynamicDialog(dialogData,
            () => {
              if (this.selectedMatchIds.length === 0 || !this.targetRingNo) return;

              this.loaderService.requestStarted('Transferring matches...');
              const payload = {
                match_ids: this.selectedMatchIds,
                new_ring_no: Number(this.targetRingNo)
              };

              this.drawsheetService.bulkShiftRing(payload).subscribe({
                next: (res: any) => {
                  this.toast.success(res.message || 'Matches transferred successfully');
                  this.loaderService.requestEnded();
                  setTimeout(() => window.location.reload(), 1000);
                },
                error: (err) => {
                  this.toast.error(err?.error?.message || 'Failed to transfer matches');
                  this.loaderService.requestEnded();
                }
              });
            },
            () => { }
          );
        },
        error: (err) => {
          this.toast.error('Failed to fetch bracket data');
          this.loaderService.requestEnded();
        }
      });
    }
  }

  isAllSelected() {
    return this.remainingMatches.length > 0 && this.selectedMatchIds.length === this.remainingMatches.length;
  }

  toggleSelectAll() {
    if (this.isAllSelected()) {
      this.selectedMatchIds = [];
    } else {
      this.selectedMatchIds = this.remainingMatches.map(m => m.match_id);
    }
  }

  toggleMatchSelection(id: number) {
    const idx = this.selectedMatchIds.indexOf(id);
    if (idx > -1) {
      this.selectedMatchIds.splice(idx, 1);
    } else {
      this.selectedMatchIds.push(id);
    }
    this.selectedMatchIds = [...this.selectedMatchIds];
  }
  // #endregion
}